import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "components/ui/Select";
import { SelectVendors, SelectBrandAndSubbrands, SelectRawItems } from "components/ui/selectors";
import Input from "components/ui/Input";
import { useGenerateItemListReport } from "http-hooks/agitem/useAgitem";
import useAuth from "hooks/useAuth";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

const reportTypeOptions = [
  { value: "I", label: "Item-wise" },
  { value: "B", label: "Brand-wise" },
];

const itemStatusOptions = [
  { value: 2, label: "All" },
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

export default function ItemListReport() {
  const navigate = useNavigate();
  const { agttp } = useAuth();

  const [vendor, setVendor]               = useState(null);
  const [boolVendGrp, setBoolVendGrp]     = useState(false);
  const [itemStatus, setItemStatus]        = useState(itemStatusOptions[0]);
  const [repType, setRepType]             = useState(null);
  const [rawItem, setRawItem]             = useState(null);
  const [brandSubbrand, setBrandSubbrand] = useState(null);

  const { mutateAsync: generateReport, isPending: isGenerating } = useGenerateItemListReport();

  const handleGenerateReport = async () => {
    if (!repType) {
      toast.error("Please select a report type");
      return;
    }

    try {
      const params = {
        reptype:     repType.value,
        itmvendorId: vendor?.ps_Id || "",
        boolVendGrp: boolVendGrp,
        itemStat:    itemStatus?.value ?? 2,
        itemId:      repType.value === "I" ? (rawItem?.itemId || "") : "",
        brandId:     repType.value === "B" ? (brandSubbrand?.id || "") : "",
      };

      const data = await generateReport(params);
      const { htmlUrl, pdfUrl } = getDocumentUrls(data);

      navigate(
        getDocumentViewerRoute("item-list", {
          htmlUrl,
          pdfUrl,
          documentNumber: "Item List",
          documentType: "Item List",
        })
      );
    } catch (error) {
      console.error("Error generating item list report:", error);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold leading-6 text-gray-900 text-center">
        Item List Generator
      </h1>

      <div className="max-w-md mx-auto mt-6 space-y-4">

        {agttp === 0 && (
          <>
            <SelectVendors
              label="Vendor"
              value={vendor}
              onChange={setVendor}
              isClearable={true}
            />
            <Input
              type="checkbox"
              label="Use Vendor Group"
              value={boolVendGrp}
              onChange={(e) => setBoolVendGrp(e.target.checked)}
            />
          </>
        )}

        <Select
          label="Item Status"
          value={itemStatus}
          onChange={setItemStatus}
          options={itemStatusOptions}
          isClearable={false}
          isSearchable={false}
        />

        <Select
          label="Report Type"
          value={repType}
          onChange={(v) => {
            setRepType(v);
            setRawItem(null);
            setBrandSubbrand(null);
          }}
          options={reportTypeOptions}
          placeholder="Choose report type..."
          isClearable={false}
          isSearchable={false}
          isRequired
        />

        {repType?.value === "I" && (
          <div>
            <SelectRawItems
              label="Item (optional — leave blank for all)"
              value={rawItem}
              onChange={setRawItem}
              showAddButton={false}
            />
          </div>
        )}

        {repType?.value === "B" && (
          <SelectBrandAndSubbrands
            label="Brand / Sub-brand"
            value={brandSubbrand}
            onChange={setBrandSubbrand}
            isRequired
            showAddButton={false}
          />
        )}

        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={handleGenerateReport}
            disabled={isGenerating || !repType}
            className="rounded-md bg-indigo-600 px-8 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>
    </div>
  );
}
